import { apiFetch, apiJson } from './http';



export type EpgSearchHit = {

  source_id: number | null;

  source_name: string;

  epg_id: string;

  display?: string;

};



export type EpgSourceRow = {

  id: number;

  name: string;

  url: string;

  is_active?: number;

  is_system_default?: number;

  is_custom?: number;

  country?: string | null;

  country_key?: string | null;

  retention_days?: number | null;

};



export async function epgSearchByQuery(q: string): Promise<EpgSearchHit[]> {

  const res = await apiFetch(`/api/epg/search.php?q=${encodeURIComponent(q)}`);

  if (!res.ok) throw new Error(`EPG search ${res.status}`);

  const data = (await res.json()) as { success?: boolean; results?: EpgSearchHit[] };

  return Array.isArray(data.results) ? data.results : [];

}



export async function fetchEpgSources(): Promise<EpgSourceRow[]> {

  const res = await apiFetch('/api/epg/sources.php');

  if (!res.ok) return [];

  const data = await res.json();

  return Array.isArray(data) ? data : [];

}



/** Lista identyfikatorów kanałów z załadowanego źródła EPG (jak legacy `loadEpgChannelList`). */
export async function fetchEpgChannelIdsForSource(sourceId: number, q?: string): Promise<string[]> {
  const qs = new URLSearchParams({ source_id: String(sourceId) });
  if (q?.trim()) qs.set('q', q.trim());
  const res = await apiFetch(`/api/epg/channels.php?${qs}`);
  if (!res.ok) return [];
  const data = (await res.json()) as { success?: boolean; data?: string[] };
  return data.success && Array.isArray(data.data) ? data.data : [];
}



/** POST — automatyczne dopasowanie EPG ID dla jednego kanału (jak legacy `autoEpgSearch('selected')`). */

export async function epgAutoMapChannel(
  channelId: number,
  channelName: string,
  sourceId?: number | null,
): Promise<string | null> {
  const body: Record<string, unknown> = { channel_id: channelId, channel_name: channelName };
  if (sourceId != null && Number.isFinite(sourceId)) {
    body.source_id = sourceId;
  }
  const data = await apiJson<{ success?: boolean; epg_id?: string }>('/api/epg/search.php', {
    method: 'POST',
    body: JSON.stringify(body),
  });

  return data.success && data.epg_id ? String(data.epg_id) : null;
}



export async function epgAutoMapSelected(

  playlistId: string,

  channelIds: number[],
  sourceId: number,

): Promise<{

  success: boolean;

  message?: string;

  total?: number;

  updated?: number;

  results?: {
    channel_id?: number;
    channel_name?: string;
    status?: string;
    epg_id?: string | null;
    source_id?: number;
    source_name?: string;
    score?: number;
    suggestions?: string[];
  }[];

}> {

  return apiJson('/api/epg/search-selected.php', {

    method: 'POST',

    body: JSON.stringify({
      playlist_id: Number(playlistId),
      channel_ids: channelIds,
      source_id: sourceId,
    }),

  });

}



export async function epgAutoMapAll(
  playlistId: string,
  sourceId: number,
): Promise<{
  success: boolean;
  count?: number;
  message?: string;
}> {
  const qs = new URLSearchParams({
    playlist_id: String(playlistId),
    source_id: String(sourceId),
  });
  return apiJson(`/api/epg/search-all.php?${qs}`);
}



export async function fetchEpgNow(

  channelId: number,

  epgId: string,

  sourceId: number,

): Promise<{ programs: Record<string, unknown>[]; message: string | null }> {
  const data = await apiJson<{
    success?: boolean;
    data?: Record<string, unknown>[];
    message?: string;
  }>(
    `/api/epg/now.php?channel_id=${channelId}&epg_id=${encodeURIComponent(epgId)}&source_id=${sourceId}`,
  );

  if (!data?.success) {
    throw new Error(data?.message || 'Now playing response error');
  }

  return {
    programs: Array.isArray(data.data) ? data.data : [],
    message: typeof data.message === 'string' && data.message.trim() !== '' ? data.message : null,
  };

}

/** Powiadom edytor EPG, by ponownie pobrał `fetchEpgSources` (jak legacy po zmianie źródeł). */
export function notifyEpgSourcesUpdated(): void {
  window.dispatchEvent(new Event('iptv-epg-sources-updated'));
}

export async function postEpgCountryDefaults(countries: string[]): Promise<{
  success: boolean;
  message?: string;
  enabled_count?: number;
}> {
  return apiJson('/api/epg/country-defaults.php', {
    method: 'POST',
    body: JSON.stringify({ countries }),
  });
}

export type EpgSettingsPayload = {
  default_source_id: number | null;
  refresh_interval: number;
  auto_refresh: number;
};

export async function fetchEpgSettings(): Promise<{
  success?: boolean;
  data?: {
    default_source_id?: number | null;
    refresh_interval?: number;
    auto_refresh?: number;
  };
}> {
  return apiJson('/api/epg/settings.php');
}

export async function saveEpgSettingsApi(
  payload: EpgSettingsPayload,
): Promise<{ success: boolean; message?: string }> {
  return apiJson('/api/epg/settings.php', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function createEpgSource(body: {
  name: string;
  url: string;
  is_active: number;
  compressed: number;
  retention_days?: number;
}): Promise<{ success: boolean; message?: string }> {
  return apiJson('/api/epg/sources.php', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function updateEpgSource(body: {
  id: number;
  retention_days?: number;
  is_active?: number;
  country?: string | null;
  language?: string | null;
}): Promise<{ success: boolean; message?: string }> {
  return apiJson('/api/epg/sources.php', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function deleteEpgSourceById(id: number): Promise<{ success: boolean; message?: string }> {
  return apiJson(`/api/epg/sources.php?id=${id}`, { method: 'DELETE' });
}

export async function triggerEpgRefreshAll(): Promise<{
  success: boolean;
  message?: string;
  sources_count?: number;
}> {
  return apiJson('/api/epg/refresh_all.php', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function triggerEpgCleanup(sourceId?: number): Promise<{
  success: boolean;
  message?: string;
  deleted_total?: number;
}> {
  const qs = sourceId && sourceId > 0 ? `?source_id=${sourceId}` : '';
  return apiJson(`/api/epg/cleanup.php${qs}`);
}

export async function runEpgBenchmark(params?: {
  playlist_id?: number;
  source_id?: number;
  runs?: number;
}): Promise<{
  success: boolean;
  message?: string;
  stats?: Record<string, unknown>;
}> {
  const qs = new URLSearchParams();
  if (params?.playlist_id) qs.set('playlist_id', String(params.playlist_id));
  if (params?.source_id) qs.set('source_id', String(params.source_id));
  if (params?.runs) qs.set('runs', String(params.runs));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiJson(`/api/epg/benchmark.php${suffix}`);
}

export async function removeUnassignedEpgIds(playlistId: number): Promise<{
  success: boolean;
  cleared?: number;
  message?: string;
}> {
  return apiJson('/api/epg/remove-empty-ids.php', {
    method: 'POST',
    body: JSON.stringify({ playlist_id: playlistId }),
  });
}

export type EpgCustomChannelRow = {
  id: number;
  source_id: number;
  channel_id: string;
  name: string;
  created_at?: string;
};

export type EpgCustomEventRow = {
  id: number;
  custom_channel_id: number;
  title: string;
  description?: string | null;
  start_time: string;
  end_time: string;
  repeat_mode: 'none' | 'every_2h' | 'daily' | 'weekly';
  created_at?: string;
};

export type EpgCustomRepeatMode = EpgCustomEventRow['repeat_mode'];

export async function fetchCustomEpgChannels(): Promise<{
  success: boolean;
  source_id?: number;
  data?: EpgCustomChannelRow[];
  message?: string;
}> {
  return apiJson('/api/epg/custom-channels.php');
}

export async function createCustomEpgChannel(body: {
  name: string;
  channel_id: string;
}): Promise<{ success: boolean; id?: number; source_id?: number; message?: string }> {
  return apiJson('/api/epg/custom-channels.php', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function deleteCustomEpgChannel(id: number): Promise<{ success: boolean; message?: string }> {
  return apiJson(`/api/epg/custom-channels.php?id=${id}`, { method: 'DELETE' });
}

export async function fetchCustomEpgEvents(customChannelId: number): Promise<{
  success: boolean;
  data?: EpgCustomEventRow[];
  message?: string;
}> {
  return apiJson(`/api/epg/custom-events.php?custom_channel_id=${customChannelId}`);
}

export async function createCustomEpgEvent(body: {
  custom_channel_id: number;
  title: string;
  description?: string;
  start_time: string;
  end_time: string;
  repeat_mode: EpgCustomRepeatMode;
}): Promise<{ success: boolean; id?: number; message?: string }> {
  return apiJson('/api/epg/custom-events.php', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function deleteCustomEpgEvent(id: number): Promise<{ success: boolean; message?: string }> {
  return apiJson(`/api/epg/custom-events.php?id=${id}`, { method: 'DELETE' });
}
